<template>
  <v-card class="guide-editor">
    <ConfirmationDialog
      ref="discardConfirmation"
      :title="$t('landingpage.customer-guide.editor.discardTitle')"
      :text="$t('landingpage.customer-guide.editor.discardText')"
      :ok-button-text="$t('landingpage.customer-guide.editor.discard')"
      color="error"
    />
    <ConfirmationDialog
      ref="submitConfirmation"
      :title="$t('landingpage.customer-guide.editor.submitTitle')"
      :text="$t('landingpage.customer-guide.editor.submitText')"
      :ok-button-text="$t('landingpage.customer-guide.editor.submit')"
    />
    <v-card-title>
      <div>
        <h3 class="text-h6">
          {{ $t('landingpage.customer-guide.editor.title') }}
        </h3>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ $t('landingpage.customer-guide.editor.subtitle') }}
        </p>
      </div>
    </v-card-title>
    <v-card-text>
      <v-alert
        type="info"
        variant="tonal"
        class="mb-4"
      >
        {{ $t('landingpage.customer-guide.editor.locked') }}
      </v-alert>
      <v-alert
        v-if="hasConflict"
        type="warning"
        variant="tonal"
        class="mb-4"
      >
        {{ $t('landingpage.customer-guide.editor.conflict') }}
      </v-alert>
      <v-alert
        v-if="hasReplacementConstraint"
        type="warning"
        variant="tonal"
        class="mb-4"
      >
        {{ $t('landingpage.customer-guide.editor.replacementInProgress') }}
      </v-alert>
      <v-progress-linear
        v-if="$apollo.queries.draft.loading"
        indeterminate
        color="primary"
      />
      <template v-else-if="localContent">
        <h4 class="text-subtitle-1 mb-2">
          {{ $t('landingpage.customer-guide.editor.cover') }}
        </h4>
        <v-text-field
          v-model="localContent.cover.title"
          :label="$t('landingpage.customer-guide.editor.coverTitle')"
          class="mb-2"
        />
        <v-text-field
          v-model="localContent.cover.badgeText"
          :label="$t('landingpage.customer-guide.editor.coverBadge')"
          class="mb-2"
        />
        <v-textarea
          v-model="localContent.cover.headline"
          :label="$t('landingpage.customer-guide.editor.coverHeadline')"
          rows="2"
          class="mb-2"
        />
        <v-textarea
          v-model="localContent.cover.subheadline"
          :label="$t('landingpage.customer-guide.editor.coverSubheadline')"
          rows="2"
          class="mb-4"
        />

        <h4 class="text-subtitle-1 mb-2">
          {{ $t('landingpage.customer-guide.editor.introduction') }}
        </h4>
        <v-text-field
          v-model="localContent.introduction.salutation"
          :label="$t('landingpage.customer-guide.editor.salutation')"
          class="mb-2"
        />
        <v-textarea
          :model-value="paragraphText(localContent.introduction.paragraphs)"
          :label="$t('landingpage.customer-guide.editor.introductionCopy')"
          :hint="$t('landingpage.customer-guide.editor.paragraphHint')"
          persistent-hint
          rows="6"
          class="mb-4"
          @update:model-value="updateParagraphs(localContent.introduction, $event)"
        />

        <h4 class="text-subtitle-1 mb-2">
          {{ $t('landingpage.customer-guide.editor.chapters') }}
        </h4>
        <v-expansion-panels class="mb-4">
          <v-expansion-panel
            v-for="(chapter, index) in localContent.chapters"
            :key="index"
          >
            <v-expansion-panel-title>
              {{ $t('landingpage.customer-guide.editor.chapter', { number: index + 1 }) }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-text-field
                v-model="chapter.title"
                :label="$t('landingpage.customer-guide.editor.chapterTitle')"
                class="mb-2"
              />
              <v-textarea
                :model-value="paragraphText(chapter.paragraphs)"
                :label="$t('landingpage.customer-guide.editor.chapterCopy')"
                :hint="$t('landingpage.customer-guide.editor.paragraphHint')"
                persistent-hint
                rows="8"
                @update:model-value="updateParagraphs(chapter, $event)"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <h4 class="text-subtitle-1 mb-2">
          {{ $t('landingpage.customer-guide.editor.cta') }}
        </h4>
        <v-text-field
          v-model="localContent.cta.headline"
          :label="$t('landingpage.customer-guide.editor.ctaHeadline')"
          class="mb-2"
        />
        <v-textarea
          v-model="localContent.cta.subtext"
          :label="$t('landingpage.customer-guide.editor.ctaSubtext')"
          rows="3"
          class="mb-2"
        />
        <v-textarea
          :model-value="lineText(localContent.cta.benefits)"
          :label="$t('landingpage.customer-guide.editor.ctaBenefits')"
          rows="4"
          class="mb-2"
          @update:model-value="updateLines(localContent.cta, 'benefits', $event)"
        />
        <v-textarea
          :model-value="paragraphText(localContent.cta.closingParagraphs)"
          :label="$t('landingpage.customer-guide.editor.ctaClosing')"
          :hint="$t('landingpage.customer-guide.editor.paragraphHint')"
          persistent-hint
          rows="6"
          @update:model-value="updateParagraphs(localContent.cta, $event, 'closingParagraphs')"
        />
      </template>
      <GuideDraftPreview
        v-if="previewPdfDataUrl"
        :pdf-data-url="previewPdfDataUrl"
      />
    </v-card-text>
    <v-card-actions>
      <v-btn
        variant="text"
        :disabled="isSaving || isPreviewing || isSubmitting"
        @click="close"
      >
        {{ $t('landingpage.customer-guide.editor.cancel') }}
      </v-btn>
      <v-btn
        variant="text"
        :disabled="!isDirty || isSaving || isPreviewing || isSubmitting"
        @click="discardLocalChanges"
      >
        {{ $t('landingpage.customer-guide.editor.discard') }}
      </v-btn>
      <v-spacer />
      <v-btn
        variant="outlined"
        :loading="isPreviewing"
        :disabled="!isContentValid || isSaving || isSubmitting"
        @click="preview"
      >
        {{ $t('landingpage.customer-guide.editor.preview') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="isSaving"
        :disabled="!isDirty || !isContentValid || isPreviewing || isSubmitting"
        @click="save"
      >
        {{ $t('landingpage.customer-guide.editor.save') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="isSubmitting"
        :disabled="!isContentValid || isSaving || isPreviewing"
        @click="submit"
      >
        {{ $t('landingpage.customer-guide.editor.submit') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import bus, { eventNames } from '@/lib/eventBus'

import CUSTOMER_GUIDE_DRAFT from './CustomerGuideDraft.gql'
import GuideDraftPreview from './GuideDraftPreview.vue'
import {
  cloneGuideDraftContent,
  isValidGuideDraftContent,
  linesFromText,
  linesToText,
  paragraphsFromText,
  paragraphsToText,
} from './guideDraftUtils'
import PREVIEW_CUSTOMER_GUIDE_DRAFT from './PreviewCustomerGuideDraft.gql'
import SAVE_CUSTOMER_GUIDE_DRAFT from './SaveCustomerGuideDraft.gql'
import SUBMIT_CUSTOMER_GUIDE_DRAFT from './SubmitCustomerGuideDraft.gql'

export default {
  components: { ConfirmationDialog, GuideDraftPreview },
  props: {
    guideMaterialId: { type: String, required: true },
  },
  emits: ['close', 'submitted', 'dirty-change'],
  data() {
    return {
      draft: null,
      localContent: null,
      previewPdfDataUrl: '',
      isSaving: false,
      isPreviewing: false,
      isSubmitting: false,
      hasConflict: false,
      hasReplacementConstraint: false,
    }
  },
  apollo: {
    draft: {
      query: CUSTOMER_GUIDE_DRAFT,
      variables() {
        return { materialId: this.guideMaterialId }
      },
      result({ data }) {
        this.draft = data.customerGuideDraft
        this.localContent = cloneGuideDraftContent(data.customerGuideDraft.content)
      },
    },
  },
  computed: {
    isDirty() {
      return this.draft && JSON.stringify(this.localContent) !== JSON.stringify(this.draft.content)
    },
    isContentValid() {
      return isValidGuideDraftContent(this.localContent)
    },
  },
  watch: {
    isDirty(value) {
      this.$emit('dirty-change', value)
    },
    localContent: {
      deep: true,
      handler() {
        this.previewPdfDataUrl = ''
      },
    },
  },
  mounted() {
    window.addEventListener('beforeunload', this.confirmBrowserNavigation)
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.confirmBrowserNavigation)
  },
  methods: {
    paragraphText: paragraphsToText,
    lineText: linesToText,
    updateParagraphs(target, value, field = 'paragraphs') {
      target[field] = paragraphsFromText(value)
    },
    updateLines(target, field, value) {
      target[field] = linesFromText(value)
    },
    confirmBrowserNavigation(event) {
      if (!this.isDirty) return
      event.preventDefault()
      event.returnValue = ''
    },
    async save() {
      if (!this.isDirty) return true
      this.isSaving = true
      this.hasConflict = false
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SAVE_CUSTOMER_GUIDE_DRAFT,
          variables: {
            input: {
              materialId: this.guideMaterialId,
              expectedRevision: this.draft.revision,
              content: this.localContent,
            },
          },
        })
        const result = data.saveCustomerGuideDraft
        this.draft = result.draft
        this.hasConflict = result.conflict
        if (result.conflict) return false
        this.localContent = cloneGuideDraftContent(result.draft.content)
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('landingpage.customer-guide.editor.saved'),
        })
        return true
      } catch (error) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('landingpage.customer-guide.editor.saveError'),
        })
        return false
      } finally {
        this.isSaving = false
      }
    },
    async preview() {
      this.isPreviewing = true
      this.hasReplacementConstraint = false
      try {
        const { data } = await this.$apollo.mutate({
          mutation: PREVIEW_CUSTOMER_GUIDE_DRAFT,
          variables: { input: { materialId: this.guideMaterialId, content: this.localContent } },
        })
        this.previewPdfDataUrl = data.previewCustomerGuideDraft.pdfDataUrl
      } catch (error) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('landingpage.customer-guide.editor.previewError'),
        })
      } finally {
        this.isPreviewing = false
      }
    },
    async submit() {
      if (!(await this.save())) return
      const confirmed = await this.$refs.submitConfirmation?.confirm()
      if (!confirmed) return

      this.isSubmitting = true
      this.hasConflict = false
      this.hasReplacementConstraint = false
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SUBMIT_CUSTOMER_GUIDE_DRAFT,
          variables: {
            input: { materialId: this.guideMaterialId, expectedRevision: this.draft.revision },
          },
        })
        const result = data.submitCustomerGuideDraft
        this.hasConflict = result.conflict
        this.hasReplacementConstraint = result.replacementInProgress
        if (result.conflict || result.replacementInProgress) return
        this.$emit('submitted', result.guide)
        this.$emit('close')
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('landingpage.customer-guide.editor.submitted'),
        })
      } catch (error) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('landingpage.customer-guide.editor.submitError'),
        })
      } finally {
        this.isSubmitting = false
      }
    },
    async discardLocalChanges() {
      const confirmed = await this.$refs.discardConfirmation?.confirm()
      if (!confirmed) return
      this.localContent = cloneGuideDraftContent(this.draft.content)
      this.previewPdfDataUrl = ''
    },
    async close() {
      if (this.isDirty) {
        const confirmed = await this.$refs.discardConfirmation?.confirm()
        if (!confirmed) return
      }
      this.$emit('close')
    },
  },
}
</script>
